import { AsyncStatus } from 'lib-shared-types-api';
import { createContext, Dispatch, SetStateAction } from 'react';
import { noop } from 'ts-essentials';

import {
  CommandLauncherAction,
  CommandLauncherHint,
  CommandLauncherPerformActionFn,
  CommandLauncherRegisterActionsFn,
  CommandLauncherScope,
} from 'utils/commandLauncher';

/** Represents the value of the command launcher context. */
export type CommandLauncherContextValue = {
  /** The currently available actions. */
  readonly actions: readonly CommandLauncherAction[];
  /** The scope of the query. */
  readonly scope?: CommandLauncherScope;
  /** The full search query. */
  readonly query: string;
  /** The query of the scope. */
  readonly scopeQuery: string;
  /** Whether the command launcher list is active. */
  readonly isActive: boolean;
  /** Whether the command launcher is open. */
  readonly isOpen: boolean;
  /**
   * The async status of an ongoing operation inside command launcher, e.g.
   * fetching data, searching, etc.
   */
  readonly asyncStatus: AsyncStatus;
  /** The type ahead text. */
  readonly typeAhead: string;
  /** The keyboard hints replacing the default ones, if any. */
  readonly hints?: readonly CommandLauncherHint[];
  /** The function for setting the keyboard hints. */
  readonly setHints: Dispatch<
    SetStateAction<readonly CommandLauncherHint[] | undefined>
  >;
  /** Whether the search input is read-only, e.g. while a scope shows a detail view. */
  readonly isInputReadOnly: boolean;
  /** The function for setting whether the search input is read-only. */
  readonly setIsInputReadOnly: Dispatch<SetStateAction<boolean>>;
  /** The element next to the search input that scopes can portal their search options into. */
  readonly inputSuffixElement: HTMLElement | null;
  /** The function for setting the element next to the search input. */
  readonly setInputSuffixElement: Dispatch<SetStateAction<HTMLElement | null>>;
  /** The function for setting the type ahead text. */
  readonly setTypeAhead: Dispatch<SetStateAction<string>>;
  /** The function for setting the async status. */
  readonly setAsyncStatus: Dispatch<SetStateAction<AsyncStatus>>;
  /** The function for setting the active state. */
  readonly setIsActive: Dispatch<SetStateAction<boolean>>;
  /** The function for setting the open state. */
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
  /** The function for setting the query. */
  readonly setQuery: Dispatch<SetStateAction<string>>;
  /** The function for registering actions. */
  readonly registerActions: CommandLauncherRegisterActionsFn;
  /** The callback for performing an action. */
  readonly performAction: CommandLauncherPerformActionFn;
};

/** Represents the command launcher context. */
export const CommandLauncherContext =
  createContext<CommandLauncherContextValue>({
    actions: [],
    asyncStatus: 'fulfilled',
    inputSuffixElement: null,
    isActive: false,
    isInputReadOnly: false,
    isOpen: false,
    query: '',
    scopeQuery: '',
    typeAhead: '',
    performAction: noop,
    registerActions: () => noop,
    setAsyncStatus: noop,
    setHints: noop,
    setInputSuffixElement: noop,
    setIsActive: noop,
    setIsInputReadOnly: noop,
    setIsOpen: noop,
    setQuery: noop,
    setTypeAhead: noop,
  });
